import React from 'react'
import classNames, { type ClassValue } from 'classnames'
import { BaseBadge } from './BaseBadge'
import { Tooltip } from '../ui/tooltip'
import {
    deliveryStatusMetaByKey,
    getDeliveryReasonMetadata,
    deliveryReasonMetaByKey,
    deliveryReasonOrderByKey,
    type DeliveryReasonMetadata,
} from '../../constants/delivery_status'
import type { Intent } from '../../types/badges'

type DeliveryStatusBadgeProps = {
    delivery_status: string | null
    delivery_reasons: string[] | null
    ad_product?: string | null
    className?: ClassValue
}

export type ResolvedDeliveryStatus = {
    label: string
    explanation: string
    intent?: Intent
    actionableReasons: DeliveryReasonMetadata[]
    inheritedReasons: DeliveryReasonMetadata[]
    informationalReasons: DeliveryReasonMetadata[]
    hasRecognisedReason: boolean
}

const orderReasons = (reasons: DeliveryReasonMetadata[]) =>
    [...reasons].sort((left, right) => {
        const priority = left.priority - right.priority
        if (priority !== 0) return priority
        return (
            (deliveryReasonOrderByKey[left.key] ?? Number.MAX_SAFE_INTEGER) -
            (deliveryReasonOrderByKey[right.key] ?? Number.MAX_SAFE_INTEGER)
        )
    })

export const resolveDeliveryStatus = (
    deliveryStatus: string | null,
    deliveryReasons: string[] | null,
): ResolvedDeliveryStatus => {
    const status = deliveryStatus ? deliveryStatusMetaByKey[deliveryStatus] : undefined
    const metadata = (deliveryReasons ?? []).map(getDeliveryReasonMetadata)
    const actionableReasons = orderReasons(
        metadata.filter((reason) => reason.class === 'actionable'),
    )
    const inheritedReasons = orderReasons(metadata.filter((reason) => reason.class === 'inherited'))
    const informationalReasons = orderReasons(
        metadata.filter((reason) => reason.class === 'informational'),
    )
    const hasRecognisedReason = (deliveryReasons ?? []).some((reason) =>
        Object.hasOwn(deliveryReasonMetaByKey, reason),
    )
    const winningReason = actionableReasons[0]
    const onlyInherited =
        inheritedReasons.length > 0 &&
        actionableReasons.length === 0 &&
        informationalReasons.length === 0
    const displayReason = actionableReasons[0] ?? informationalReasons[0] ?? inheritedReasons[0]

    return {
        label: status?.label ?? displayReason?.label ?? deliveryStatus ?? '',
        explanation:
            status?.explanation ??
            (deliveryStatus
                ? `Amazon reported an unrecognised delivery status: ${deliveryStatus}.`
                : 'Amazon did not provide a delivery status.'),
        intent: onlyInherited ? undefined : (winningReason?.intent ?? status?.intent),
        actionableReasons,
        inheritedReasons,
        informationalReasons,
        hasRecognisedReason,
    }
}

const ReasonSection = ({
    title,
    reasons,
    muted = false,
}: {
    title: string
    reasons: DeliveryReasonMetadata[]
    muted?: boolean
}) => {
    if (!reasons.length) return null
    return (
        <div className={classNames({ 'text-slate-400': muted })}>
            <strong>{title}</strong>
            <ul className="m-0 pl-4">
                {reasons.map((reason) => (
                    <li key={reason.key}>
                        <span>{reason.label}: </span>
                        {reason.explanation}
                    </li>
                ))}
            </ul>
        </div>
    )
}

export const DeliveryStatusBadge = ({
    delivery_status,
    delivery_reasons,
    ad_product,
    className,
}: DeliveryStatusBadgeProps) => {
    if (ad_product === 'SPONSORED_BRANDS' || ad_product === 'SPONSORED_DISPLAY') {
        return (
            <Tooltip content="Sponsored Products only" position="top">
                <span className={className}>—</span>
            </Tooltip>
        )
    }

    if (!delivery_status && !delivery_reasons?.length) return null

    const resolved = resolveDeliveryStatus(delivery_status, delivery_reasons)
    const unrecognisedNotDelivering =
        delivery_status === 'NOT_DELIVERING' && !resolved.hasRecognisedReason
    const badge = (
        <BaseBadge
            options={{ status: { text: resolved.label, intent: resolved.intent } }}
            value="status"
            className={className}
        />
    )
    const tooltip = (
        <div style={{ maxWidth: '360px' }}>
            <div>{resolved.explanation}</div>
            {unrecognisedNotDelivering && (
                <div>Amazon did not provide a recognised delivery reason.</div>
            )}
            <ReasonSection title="Needs attention" reasons={resolved.actionableReasons} />
            <ReasonSection title="Other information" reasons={resolved.informationalReasons} />
            <ReasonSection
                title="Inherited from a parent"
                reasons={resolved.inheritedReasons}
                muted
            />
        </div>
    )

    return (
        <Tooltip content={tooltip} position="top">
            {badge}
        </Tooltip>
    )
}
